import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { CommandPalette } from './CommandPalette';
import { useRealtime } from '@/hooks/useRealtime';
import { KuotaHabisDialog } from '@/components/billing/KuotaHabisDialog';

/**
 * Judul halaman per rute, dipakai topbar dan judul tab peramban.
 *
 * Kolom ketiga adalah alamat INDUKNYA, dan hanya diisi untuk halaman yang
 * memang punya induk. Dari situlah jejak lokasi di topbar disusun: "Detail
 * Job" saja tidak memberi tahu job yang mana maupun bagaimana kembali ke
 * daftarnya, sedangkan "List Job / Detail Job" menjawab keduanya.
 */
const TITLES = [
  [/^\/dashboard/, 'Dashboard'],
  [/^\/jobs\/new/, 'Buat Scraping', '/jobs'],
  [/^\/jobs\/running/, 'Scraping Saat Ini', '/jobs'],
  [/^\/jobs\/[^/]+\/runs\//, 'Detail Run', '/jobs'],
  [/^\/jobs\/[^/]+/, 'Detail Job', '/jobs'],
  [/^\/jobs/, 'List Job'],
  [/^\/scheduler/, 'Scheduler'],
  [/^\/datasets/, 'Data Publik'],
  [/^\/storage/, 'Penyimpanan Pribadi'],
  [/^\/analisa\/eda/, 'EDA', '/analisa'],
  [/^\/analisa\/analytics/, 'Analytics', '/analisa'],
  [/^\/analisa/, 'Analisa'],
  [/^\/lapor-bug\/[^/]+/, 'Detail Laporan', '/lapor-bug'],
  [/^\/lapor-bug/, 'Lapor Bug'],
  [/^\/settings/, 'Pengaturan'],
  [/^\/billing/, 'Paket & Tagihan'],

  // Wilayah Manajemen Web dan Dev.
  //
  // Seluruh halaman ini dulu tidak punya judul sama sekali, jadi tab peramban
  // menuliskan "Crawel - Crawel" dan bilah atas menuliskan "Crawel" untuk
  // sebelas halaman yang berbeda. Labelnya sengaja disamakan persis dengan
  // yang tertulis di sidebar: melihat "Harga Paket" di menu lalu "Crawel" di
  // atas halamannya membuat orang ragu apakah tautannya salah.
  [/^\/manajemen\/pengguna/, 'Pengguna'],
  [/^\/manajemen\/pelanggan/, 'Data Pelanggan'],
  [/^\/manajemen\/harga/, 'Harga Paket'],
  [/^\/manajemen\/addon/, 'Add-on'],
  [/^\/manajemen\/diskon/, 'Kode Diskon'],
  [/^\/manajemen\/transaksi/, 'Transaksi'],
  [/^\/manajemen\/laporan/, 'Laporan'],
  [/^\/manajemen\/audit/, 'Jejak Audit'],
  [/^\/manajemen\/konten/, 'Konten Landing'],
  [/^\/manajemen\/proxy/, 'Proxy'],
  [/^\/manajemen\/pengaturan/, 'Pengaturan Situs'],
  [/^\/dev\/templates/, 'Template Skrip'],

];

const cariRute = (pathname) => TITLES.find(([pattern]) => pattern.test(pathname));

const resolveTitle = (pathname) => cariRute(pathname)?.[1] ?? 'Crawel';

/**
 * Jejak lokasi untuk topbar.
 *
 * Label induknya dicari lewat daftar yang sama, jadi mengganti nama sebuah
 * halaman cukup dilakukan di satu tempat dan jejaknya ikut berubah.
 */
const resolveJejak = (pathname) => {
  const rute = cariRute(pathname);
  if (!rute) return [{ label: 'Crawel' }];

  const [, label, indukPath] = rute;
  if (!indukPath) return [{ label }];

  const labelInduk = cariRute(indukPath)?.[1];
  // Induk yang tidak punya judul dilewati saja. Menampilkan alamat mentah
  // sebagai label lebih membingungkan daripada tidak menampilkan apa-apa.
  if (!labelInduk) return [{ label }];

  return [{ label: labelInduk, to: indukPath }, { label }];
};

export const AppLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { pathname } = useLocation();

  const title = resolveTitle(pathname);
  const jejak = resolveJejak(pathname);

  // Koneksi realtime dipasang sekali di sini, bukan per-halaman.
  useRealtime();

  useEffect(() => {
    document.title = `${title} · Crawel`;
  }, [title]);

  // Menu geser ditutup setiap kali rute berubah - kalau tidak, ia menempel
  // di layar setelah pengguna menekan sebuah tautan.
  useEffect(() => setSidebarOpen(false), [pathname]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="min-h-screen bg-[var(--surface-canvas)]">
      {/* Tautan lewati - pengguna keyboard bisa langsung ke konten tanpa
          menyusuri seluruh sidebar setiap kali berpindah halaman. */}
      <a
        href="#konten-utama"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[70] focus:rounded-lg focus:bg-brand-950 focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Lewati ke konten utama
      </a>

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="lg:pl-64">
        <Topbar
          jejak={jejak}
          onMenuClick={() => setSidebarOpen(true)}
          onSearchClick={() => setPaletteOpen(true)}
        />

        <main id="konten-utama" className="px-4 pt-2 pb-6 lg:px-6 lg:pb-8">
          <div className="mx-auto max-w-[1400px]">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />

      {/* Dipasang sekali di sini, bukan di tiap halaman. Kuota bisa mentok di
          mana saja, dan jawabannya selalu sama. */}
      <KuotaHabisDialog />
    </div>
  );
};
